import DeleteIcon from "@mui/icons-material/Delete";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import DoneIcon from "@mui/icons-material/Done";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import axiosInstance from "../utils/axiosInstance";
import { checkPermission } from "../utils/checkPermission";
import { formatCustomDate, formatNumber } from "../utils/formatters";
import ConfirmationModal from "./ConfirmationModal";

export default function PollCard({ poll, onDelete, onUpdate }) {
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleModalClose = () => {
    setOpenConfirm(false);
  };

  const totalVotes = poll.options.reduce(
    (sum, option) => sum + option.votes,
    0,
  );

  const handleDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/polls/${poll.id}`,
      );
      if (response.status === 200) {
        onDelete();
        onUpdate();
        swal("Poll deleted successfully!", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete poll. Please try again.", { icon: "error" });
    }
  };

  const approveOrRejectPoll = async (is_approved) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/polls/${poll.id}/approve-or-reject`,
        {
          is_approved: is_approved,
        },
      );

      if (response.status === 200) {
        onUpdate();
        swal("Approved status successfully!", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update approved status!", {
        icon: "error",
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={poll.title}
          subheader={`Posted by ${poll.user.full_name} on ${formatCustomDate(poll.post_date)}`}
        />
        <CardContent>
          {poll.options.map((option) => {
            const votePercentage =
              totalVotes > 0
                ? formatNumber((option.votes / totalVotes) * 100, 2)
                : 0;
            const totalVoters = option.voters.length;

            // Show first 5 voters and remaining count
            const displayedVoters = option.voters.slice(0, 5);
            const remainingVoters = totalVoters > 5 ? totalVoters - 5 : 0;

            return (
              <Box key={option.id} sx={{ marginTop: 2 }}>
                <Typography variant="subtitle1">{`${votePercentage}% - ${option.option}`}</Typography>

                <LinearProgress
                  variant="determinate"
                  value={votePercentage}
                  sx={{ height: 10, borderRadius: 5, marginTop: 1 }}
                />

                {totalVoters > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ marginTop: 1, display: "block" }}
                  >
                    {displayedVoters.map((voter) => voter.full_name).join(", ")}
                    {remainingVoters > 0 && `, +${remainingVoters} more voters`}
                  </Typography>
                )}
              </Box>
            );
          })}
        </CardContent>
        <CardActions>
          {checkPermission("poll-update") && (
            <>
              {poll.is_approved !== true && (
                <Tooltip title="Approve">
                  <IconButton
                    color="error"
                    onClick={() => approveOrRejectPoll(true)}
                  >
                    <DoneIcon />
                  </IconButton>
                </Tooltip>
              )}
              {(poll.is_approved === true || poll.is_approved === null) && (
                <Tooltip title="Reject">
                  <IconButton
                    color="error"
                    onClick={() => approveOrRejectPoll(false)}
                  >
                    <DoDisturbIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {checkPermission("poll-delete") && (
            <Tooltip title="Delete">
              <IconButton
                color="error"
                onClick={() => {
                  setOpenConfirm(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </CardActions>
      </Card>

      {openConfirm && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleDelete}
          title={`Are you sure you want to delete this poll?`}
          message={
            <span>
              The poll will be delete. Note that this action is not reversible.
            </span>
          }
        />
      )}
    </>
  );
}
